import { useCallback, useEffect, useMemo, useState } from "react";

type StoredPrefs<TId extends string> = {
  order: TId[];
  hidden: TId[];
  rowsPerPage: number;
  /** Per-column pixel width overrides from manual resize. Unset columns keep
   *  their default (from the column definition). */
  widths: Partial<Record<TId, number>>;
};

type Options<TId extends string> = {
  columnIds: readonly TId[];
  defaultHidden?: readonly TId[];
  defaultRowsPerPage?: number;
  /**
   * Columns pinned to the start of the order. They cannot be moved before each
   * other (declared order wins) or displaced by drag-and-drop. Their hide
   * behavior is independent — drive that via `column.required` in the menu.
   */
  lockedFirst?: readonly TId[];
};

const STORAGE_PREFIX = "table-prefs:";

function enforceLocked<TId extends string>(
  order: readonly TId[],
  lockedFirst: readonly TId[] | undefined,
): TId[] {
  if (!lockedFirst || lockedFirst.length === 0) return [...order];
  const lockedSet = new Set(lockedFirst);
  const lockedInDeclaredOrder = lockedFirst.filter((id) => order.includes(id));
  const rest = order.filter((id) => !lockedSet.has(id));
  return [...lockedInDeclaredOrder, ...rest];
}

function buildDefaults<TId extends string>(options: Options<TId>): StoredPrefs<TId> {
  return {
    order: enforceLocked([...options.columnIds], options.lockedFirst),
    hidden: options.defaultHidden ? [...options.defaultHidden] : [],
    rowsPerPage: options.defaultRowsPerPage ?? 50,
    widths: {},
  };
}

/**
 * Persistent table column preferences — order, visibility, rows-per-page.
 * Storage is local (per browser); backend sync is a future swap.
 *
 * Adding a column id to `options.columnIds` automatically surfaces it on
 * existing stored prefs (appended to the end). Removing an id silently drops
 * it from stored order/hidden.
 */
export function useTableColumnPrefs<TId extends string>(storageKey: string, options: Options<TId>) {
  const defaults = useMemo(() => buildDefaults(options), [options]);
  const [prefs, setPrefs] = useState<StoredPrefs<TId>>(defaults);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const raw = window.localStorage.getItem(STORAGE_PREFIX + storageKey);
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as Partial<StoredPrefs<TId>>;
        const known = new Set(options.columnIds);
        const storedOrder = (parsed.order ?? []).filter((id) => known.has(id as TId)) as TId[];
        const missing = options.columnIds.filter((id) => !storedOrder.includes(id));
        const hidden = (parsed.hidden ?? []).filter((id) => known.has(id as TId)) as TId[];
        const widths = Object.fromEntries(
          Object.entries(parsed.widths ?? {}).filter(
            ([id, px]) => known.has(id as TId) && typeof px === "number" && Number.isFinite(px),
          ),
        ) as Partial<Record<TId, number>>;
        setPrefs({
          order: enforceLocked([...storedOrder, ...missing] as TId[], options.lockedFirst),
          hidden,
          rowsPerPage: parsed.rowsPerPage ?? defaults.rowsPerPage,
          widths,
        });
      } catch {
        // ignore — keep defaults
      }
    }
    setHydrated(true);
  }, [storageKey, options.columnIds, options.lockedFirst, defaults.rowsPerPage]);

  useEffect(() => {
    if (!hydrated || typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_PREFIX + storageKey, JSON.stringify(prefs));
  }, [storageKey, prefs, hydrated]);

  const setOrder = useCallback(
    (order: TId[]) => {
      setPrefs((p) => ({ ...p, order: enforceLocked(order, options.lockedFirst) }));
    },
    [options.lockedFirst],
  );

  const toggleHidden = useCallback((id: TId) => {
    setPrefs((p) => ({
      ...p,
      hidden: p.hidden.includes(id) ? p.hidden.filter((x) => x !== id) : [...p.hidden, id],
    }));
  }, []);

  const setRowsPerPage = useCallback((n: number) => {
    setPrefs((p) => ({ ...p, rowsPerPage: n }));
  }, []);

  const setWidth = useCallback((id: TId, px: number) => {
    setPrefs((p) => ({ ...p, widths: { ...p.widths, [id]: px } }));
  }, []);

  const reset = useCallback(() => {
    setPrefs(defaults);
  }, [defaults]);

  const hiddenSet = useMemo(() => new Set(prefs.hidden), [prefs.hidden]);

  return {
    order: prefs.order,
    hidden: hiddenSet,
    rowsPerPage: prefs.rowsPerPage,
    widths: prefs.widths,
    setOrder,
    toggleHidden,
    setRowsPerPage,
    setWidth,
    reset,
  };
}
